import config from "../config";
import events from "../constants/events";
import type { CustomerContext, HttpError } from "../types";

function httpSend(
  method: string,
  url: string,
  customer: CustomerContext,
  body: Document | XMLHttpRequestBodyInit | null,
  contentType = "application/json",
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.addEventListener("load", () => {
      if (request.readyState === 4) {
        if (request.status >= 200 && request.status < 300) {
          resolve(request.responseText);
        } else {
          reject({
            status: request.status,
            statusText: request.statusText,
            response: request.responseText,
          } satisfies HttpError);
        }
      }
    });

    request.addEventListener("error", () => {
      if (request.readyState === 4) {
        reject({
          status: request.status,
          statusText: request.statusText,
          response: request.responseText,
        } satisfies HttpError);
      }
    });

    request.overrideMimeType(contentType);
    request.open(method, url, true);
    request.setRequestHeader("Content-Type", contentType);
    request.setRequestHeader("Authorization", `bearer ${customer.token ?? ""}`);
    request.setRequestHeader("person-id", customer.personId ?? "");
    request.setRequestHeader("culture-code", customer.cultureCode ?? "");

    request.send(body);
  });
}

function httpFetchBlob(
  method: string,
  url: string,
  customer: CustomerContext,
  body: Document | XMLHttpRequestBodyInit | null,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.addEventListener("load", () => {
      if (request.readyState === 4) {
        resolve(request.response as Blob);
      }
    });

    request.addEventListener("error", () => {
      if (request.readyState === 4) {
        reject(request.response);
      }
    });

    request.open(method, url, true);
    request.responseType = "blob";

    request.setRequestHeader("Authorization", `bearer ${customer.token ?? ""}`);
    request.setRequestHeader("person-id", customer.personId ?? "");
    request.setRequestHeader("culture-code", customer.cultureCode ?? "");

    request.send(body);
  });
}

function httpSendFile(
  method: string,
  url: string,
  customer: CustomerContext,
  formData: FormData,
  uploadId: string,
  onProgress: (percentage: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    const onCancel = (cancelEvent: Event) => {
      const eventWithDetail = cancelEvent as CustomEvent<{
        uploadId?: string;
      }>;
      if (eventWithDetail.detail?.uploadId === uploadId) {
        request.abort();
        reject(new Error(config.attachments.uploadCancelledByUserException));
      }
    };

    request.addEventListener("load", () => {
      if (request.readyState === 4) {
        resolve(request.responseText);
      }
    });

    request.addEventListener("error", () => {
      if (request.readyState === 4) {
        reject(new Error(request.statusText));
      }
    });

    request.upload.addEventListener("progress", (progressEvent) => {
      const percentage =
        progressEvent.total > 0
          ? progressEvent.loaded / progressEvent.total
          : 0;
      onProgress(percentage);
    });

    window.addEventListener("cancelUpload", onCancel);
    request.open(method, url, true);
    request.setRequestHeader("Authorization", `bearer ${customer.token ?? ""}`);
    request.setRequestHeader("person-id", customer.personId ?? "");
    request.setRequestHeader("culture-code", customer.cultureCode ?? "");
    request.send(formData);
  });
}

export const httpAsync = {
  send: async (
    method: string,
    url: string,
    customer: CustomerContext,
    body: BodyInit | null,
    contentType: string,
  ): Promise<Response> => {
    const response = await fetch(url, {
      method,
      cache: "no-cache",
      headers: {
        "Content-Type": contentType,
        Authorization: `bearer ${customer.token ?? ""}`,
        "person-id": customer.personId ?? "",
        "culture-code": customer.cultureCode ?? "",
      },
      redirect: "follow",
      body,
    });

    if (response.status === 401) {
      window.dispatchEvent(new Event(events.unauthorized));
    }

    return response;
  },

  post: async (
    url: string,
    customer: CustomerContext,
    body: string,
  ): Promise<Response> =>
    httpAsync.send("POST", url, customer, body, "application/json"),
};

export const httpGet = (
  url: string,
  customer: CustomerContext,
): Promise<string> => httpSend("GET", url, customer, null, "application/json");

export const httpPost = (
  url: string,
  customer: CustomerContext,
  body: string | null = null,
): Promise<string> => httpSend("POST", url, customer, body, "application/json");

export const httpPostFile = (
  url: string,
  customer: CustomerContext,
  formData: FormData,
  uploadId: string,
  onProgress: (percentage: number) => void,
): Promise<string> =>
  httpSendFile("POST", url, customer, formData, uploadId, onProgress);

export const httpGetBlob = (
  url: string,
  customer: CustomerContext,
): Promise<Blob> => httpFetchBlob("GET", url, customer, null);
